import { useMemo } from 'react';
import { CheckCircleIcon } from '@nimbus-ds/icons';
import { useNavegate } from 'App/hooks';
import {
  HeaderContent,
  HeaderTop,
  IonPageStratus,
} from 'commons/components/IonPageStratus';
import { useAsyncFunc, useForm, useToast } from 'commons/hooks';
import { isEnumValue } from 'commons/utils';
import { logMoreDiscountsModalOptionCreated } from 'domains/Marketing/components/MoreDiscounts';
import CouponForm, {
  CouponErrorsInterface,
  CouponValuesInterface,
  validationSchema,
  initialValues as defaultInitialValues,
} from 'domains/Marketing/Coupons/components/CouponForm';
import {
  CouponCustomerLimitTypes,
  CouponDateTypes,
} from 'domains/Marketing/Coupons/components/CouponForm/types';
import useCoupons from 'domains/Marketing/Coupons/hooks/useCoupons';
import { CouponPageModes } from 'domains/Marketing/Coupons/pages/types';
import { CUSTOMER_LIMIT_TYPE_QUERY_PARAM } from 'domains/Marketing/Coupons/utils';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import {
  DATE_TYPE_QUERY_PARAM,
  DISCOUNT_OPTION_ID_QUERY_PARAM,
  MARKETING_ROUTES,
} from 'domains/Marketing/marketingRoutes';

const CURRENT_USES = 0;

function NewCouponPage() {
  const { goBack, goTo, getQueryParam } = useNavegate();
  const { createCoupon } = useCoupons();
  const { addToast } = useToast();
  const t = useTranslationMarketing('coupons.newCoupon');

  const discountOptionId = getQueryParam(DISCOUNT_OPTION_ID_QUERY_PARAM);

  const [handleCreateCoupon, isLoading, isError, clearError, errorMessage] =
    useAsyncFunc<CouponValuesInterface, Promise<void>>(async (data) => {
      if (data) {
        await createCoupon(data);
        if (discountOptionId !== null) {
          logMoreDiscountsModalOptionCreated(discountOptionId);
        }
        addToast({
          label: t('success'),
          appearance: 'success',
        });
        goTo(MARKETING_ROUTES.couponList);
      }
    });

  const dateTypeQueryParam = getQueryParam(DATE_TYPE_QUERY_PARAM);
  const customerLimitTypeQueryParam = getQueryParam(
    CUSTOMER_LIMIT_TYPE_QUERY_PARAM,
  );

  const initialValues = useMemo(
    () => ({
      ...defaultInitialValues,
      ...(isEnumValue(dateTypeQueryParam, CouponDateTypes) && {
        dateType: dateTypeQueryParam,
      }),
      ...(isEnumValue(
        customerLimitTypeQueryParam,
        CouponCustomerLimitTypes,
      ) && {
        clientLimitationType: customerLimitTypeQueryParam,
      }),
    }),
    [dateTypeQueryParam, customerLimitTypeQueryParam],
  );

  const {
    errors,
    values,
    isDirty,
    isValidating,
    handleOnChange,
    handleOnSubmit,
    setFieldValue,
  } = useForm<CouponValuesInterface, CouponErrorsInterface>({
    initialValues,
    validationSchema: validationSchema(CURRENT_USES),
    onSubmit: handleCreateCoupon,
  });

  const headerAction = {
    onClick: handleOnSubmit,
    children: t('submit'),
    icon: CheckCircleIcon,
    spinner: isValidating || isLoading,
    disabled: isValidating || isLoading,
  };

  return (
    <IonPageStratus
      width="small"
      headerTop={
        <HeaderTop mainAction={headerAction} navigation={{ onClick: goBack }} />
      }
      headerContent={<HeaderContent title={t('title')} />}
      headerFixed
    >
      <CouponForm
        setFieldValue={setFieldValue}
        values={values}
        errors={errors}
        isValidating={isValidating}
        isLoading={isLoading}
        handleOnChange={handleOnChange}
        handleOnSubmit={handleOnSubmit}
        isError={isError}
        errorMessageTranslation={t('error')}
        clearError={clearError}
        errorMessage={errorMessage}
        isDirty={isDirty}
        showActiveInput={false}
        couponIsUsed={false}
        currentUses={CURRENT_USES}
        confirmText={t('submit')}
        mode={CouponPageModes.NEW}
      />
    </IonPageStratus>
  );
}

export default NewCouponPage;
